import {
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useCallback, useMemo } from "react";
import { useDeepCompareMemoize } from "use-deep-compare-effect";
import { useLocalStorageState } from "hooks/useLocalStorageState";

interface UseGridPersistenceOptions {
  persistenceKey: string;
  initialSortModel: GridSortModel;
  initialPageSize: number;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  isMobile?: boolean;
}

interface LocalStorageData {
  pageSize: number;
  sortModel: GridSortModel;
  columnVisibilityModel: GridColumnVisibilityModel;
  columnWidthModel: Record<string, number>;
  // Screen-size-aware settings
  mobile?: {
    columnVisibilityModel: GridColumnVisibilityModel;
    columnWidthModel: Record<string, number>;
  };
  desktop?: {
    columnVisibilityModel: GridColumnVisibilityModel;
    columnWidthModel: Record<string, number>;
  };
}

/**
 * useGridPersistence manages the data grid settings in local storage
 * with support for separate mobile and desktop settings
 */
export function useGridPersistence({
  persistenceKey,
  initialSortModel,
  initialPageSize,
  initialColumnVisibilityModel = {},
  isMobile = false,
}: UseGridPersistenceOptions) {
  const [settings, setSettings] = useLocalStorageState<LocalStorageData>(
    `grid-settings-${persistenceKey}`,
    {
      defaultValue: {
        pageSize: initialPageSize,
        sortModel: initialSortModel,
        columnVisibilityModel: {},
        columnWidthModel: {},
        mobile: {
          columnVisibilityModel: {},
          columnWidthModel: {},
        },
        desktop: {
          columnVisibilityModel: {},
          columnWidthModel: {},
        },
      },
    }
  );

  // Get screen-size-specific settings
  const screenSizeKey = isMobile ? "mobile" : "desktop";
  const currentScreenSettings = useMemo(() => {
    const screenSettings = settings?.[screenSizeKey];
    return {
      columnVisibilityModel: screenSettings?.columnVisibilityModel || {},
      columnWidthModel: screenSettings?.columnWidthModel || {},
    };
  }, [settings, screenSizeKey]);

  // Merge initial column visibility model with persisted settings for current screen size
  // User preferences (persisted) take priority over initial settings
  const mergedColumnVisibilityModel = useMemo(() => {
    const persistedModel = currentScreenSettings.columnVisibilityModel || {};
    const merged = { ...initialColumnVisibilityModel };

    // Apply persisted user preferences on top of initial settings
    // Check if persistedModel exists and is an object before using Object.keys
    if (persistedModel && typeof persistedModel === "object") {
      Object.keys(persistedModel).forEach((field) => {
        merged[field] = persistedModel[field];
      });
    }

    return merged;
  }, [
    currentScreenSettings.columnVisibilityModel,
    initialColumnVisibilityModel,
  ]);

  const setPageSize = useCallback(
    (pageSize: number) => {
      setSettings((prev) => ({
        ...prev,
        pageSize,
        paginationModel: undefined,
      }));
    },
    [setSettings]
  );

  const setSortModel = useCallback(
    (model: GridSortModel) => {
      setSettings((prev) => ({ ...prev, sortModel: model }));
    },
    [setSettings]
  );

  const resetSortModel = useCallback(() => {
    setSettings((prev) => ({ ...prev, sortModel: initialSortModel }));
  }, [initialSortModel, setSettings]);

  const setColumnVisibilityModel = useCallback(
    (model: GridColumnVisibilityModel) => {
      setSettings((prev) => ({
        ...prev,
        [screenSizeKey]: {
          ...prev?.[screenSizeKey],
          columnVisibilityModel: model,
        },
      }));
    },
    [setSettings, screenSizeKey]
  );

  const setColumnWidthModel = useCallback(
    ({ colDef: { field }, width }: GridColumnResizeParams) => {
      setSettings((prev) => ({
        ...prev,
        [screenSizeKey]: {
          ...prev?.[screenSizeKey],
          columnWidthModel: {
            ...prev?.[screenSizeKey]?.columnWidthModel,
            [field]: width,
          },
          // Ensure columnVisibilityModel is preserved
          columnVisibilityModel:
            prev?.[screenSizeKey]?.columnVisibilityModel || {},
        },
      }));
    },
    [setSettings, screenSizeKey]
  );

  const unsetColumnWidthModel = useCallback(
    (field: string) => {
      setSettings((prev) => {
        const updated = { ...prev?.[screenSizeKey]?.columnWidthModel };
        delete updated[field];

        return {
          ...prev,
          [screenSizeKey]: {
            ...prev?.[screenSizeKey],
            columnWidthModel: updated,
            // Ensure columnVisibilityModel is preserved
            columnVisibilityModel:
              prev?.[screenSizeKey]?.columnVisibilityModel || {},
          },
        };
      });
    },
    [setSettings, screenSizeKey]
  );

  const resetColumnSettings = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      [screenSizeKey]: {
        ...prev?.[screenSizeKey],
        columnVisibilityModel: {},
        columnWidthModel: {},
      },
    }));
  }, [setSettings, screenSizeKey]);

  // Stabilize the initialSortModel reference. When passed inline from JSX
  // (e.g. `initialSortModel={[{ field: "deadline", sort: "asc" }]}`), the
  // parent creates a new array on every render. MUI DataGrid detects reference
  // changes to sortModel as a "sort model change" and resets the page to 0,
  // which breaks server-side pagination.
  const stableInitialSortModel = useDeepCompareMemoize(initialSortModel);

  const storedSortModel = settings?.sortModel;
  const resolvedSortModel = storedSortModel?.length
    ? storedSortModel
    : stableInitialSortModel;

  return {
    pageSize: settings?.pageSize ?? initialPageSize,
    sortModel: resolvedSortModel,
    columnVisibilityModel: mergedColumnVisibilityModel,
    columnWidthModel: currentScreenSettings.columnWidthModel || {},
    setPageSize,
    setSortModel,
    resetSortModel,
    setColumnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    resetColumnSettings,
  };
}
